/**
 * The hero's device shot.
 *
 * Frame geometry follows the iOS frame in the source design (402x775, r48,
 * 126x37 dynamic island, 139x5 home indicator), scaled to 0.75. The 58px bar
 * above the screenshot stands in for the area behind the status bar — the
 * capture starts at the app's own header — and its colour matches it, so the
 * two read as one surface.
 *
 * Frame height is not arbitrary: 58 + 402x(1334/750) = 773, so the screenshot
 * lands flush at the bottom of the frame. Changing the capture's aspect ratio
 * means changing FRAME_H with it.
 */

const FRAME_W = 402;
const FRAME_H = 775;
const SCALE = 0.75;
const HEADER_H = 58;

const SHOT = '/screenshots/ranking-screen.png';
const SHOT_W = 750;
const SHOT_H = 1334;

/**
 * Ported from the design's iOS frame, values intact. The 154px gap is what
 * parts the two halves around the dynamic island — without it the clock runs
 * under the island and the status icons get squeezed. The system font stack is
 * deliberate too: this is iOS chrome, not app type, so it must not inherit
 * Inter. Explicit flexShrink keeps the icons at their drawn size.
 */
function StatusBar() {
  const ICON = { flexShrink: 0 } as const;
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        display: 'flex',
        gap: 154,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '21px 24px 19px',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <div
        style={{
          flex: 1,
          height: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 1.5,
        }}
      >
        <span
          style={{
            fontFamily: '-apple-system, "SF Pro", system-ui',
            fontWeight: 590,
            fontSize: 17,
            lineHeight: '22px',
            color: '#fff',
          }}
        >
          9:41
        </span>
      </div>
      <div
        style={{
          flex: 1,
          height: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          paddingTop: 1,
          paddingRight: 1,
        }}
      >
        <svg width="19" height="12" viewBox="0 0 19 12" style={ICON} aria-hidden="true">
          <rect x="0" y="7.5" width="3.2" height="4.5" rx=".7" fill="#fff" />
          <rect x="4.8" y="5" width="3.2" height="7" rx=".7" fill="#fff" />
          <rect x="9.6" y="2.5" width="3.2" height="9.5" rx=".7" fill="#fff" />
          <rect x="14.4" y="0" width="3.2" height="12" rx=".7" fill="#fff" />
        </svg>
        <svg width="17" height="12" viewBox="0 0 17 12" style={ICON} aria-hidden="true">
          <path d="M8.5 3.2C10.8 3.2 12.9 4.1 14.4 5.6L15.5 4.5C13.7 2.7 11.2 1.5 8.5 1.5C5.8 1.5 3.3 2.7 1.5 4.5L2.6 5.6C4.1 4.1 6.2 3.2 8.5 3.2Z" fill="#fff" />
          <path d="M8.5 6.8C9.9 6.8 11.1 7.3 12 8.2L13.1 7.1C11.8 5.9 10.2 5.1 8.5 5.1C6.8 5.1 5.2 5.9 3.9 7.1L5 8.2C5.9 7.3 7.1 6.8 8.5 6.8Z" fill="#fff" />
          <circle cx="8.5" cy="10.5" r="1.5" fill="#fff" />
        </svg>
        <svg width="27" height="13" viewBox="0 0 27 13" style={ICON} aria-hidden="true">
          <rect x=".5" y=".5" width="23" height="12" rx="3.5" stroke="#fff" strokeOpacity=".35" fill="none" />
          <rect x="2" y="2" width="20" height="9" rx="2" fill="#fff" />
          <path d="M25 4.5V8.5C25.8 8.2 26.5 7.2 26.5 6.5C26.5 5.8 25.8 4.8 25 4.5Z" fill="#fff" fillOpacity=".4" />
        </svg>
      </div>
    </div>
  );
}

export function PhoneMock() {
  return (
    <div style={{ width: FRAME_W * SCALE, height: FRAME_H * SCALE }}>
      <div style={{ transform: `scale(${SCALE})`, transformOrigin: 'top left' }}>
        <div
          className="relative overflow-hidden bg-black"
          style={{
            width: FRAME_W,
            height: FRAME_H,
            borderRadius: 48,
            boxShadow: '0 40px 80px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.08)',
          }}
        >
          {/* dynamic island */}
          <div
            className="absolute left-1/2 -translate-x-1/2 bg-black z-50"
            style={{ top: 11, width: 126, height: 37, borderRadius: 24 }}
          />
          <StatusBar />

          <div className="h-full flex flex-col" style={{ background: '#0B1120' }}>
            <div className="shrink-0" style={{ height: HEADER_H, background: '#131C31' }} />
            <img
              src={SHOT}
              width={SHOT_W}
              height={SHOT_H}
              alt="Classificação do ranking Panela Beach no BT Vision, com os jogadores ordenados por pontos"
              className="w-full block"
            />
          </div>

          {/* home indicator */}
          <div className="absolute bottom-0 left-0 right-0 z-[60] h-[34px] flex justify-center items-end pb-2 pointer-events-none">
            <div className="w-[139px] h-[5px] rounded-full bg-white/70" />
          </div>
        </div>
      </div>
    </div>
  );
}
